import { AxiosResponse } from 'axios';
import { SharedErrors } from 'web_core_library';
import { IAssessmentConfigOPXP } from '../base/types';

export class CyclesError extends SharedErrors.BaseError {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, CyclesError.prototype);
    this.feature = 'Cycles';
  }
}

export class CyclesDBError extends SharedErrors.BaseError {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, CyclesDBError.prototype);
    this.feature = 'Cycles/DB';
  }
}

export enum ECycleStatus {
  None = 'None',
  AssessmentRunning = 'AssessmentRunning',
  AssessmentFinished = 'AssessmentFinished',
  SessionRunning = 'SessionRunning',
  SessionFinished = 'SessionFinished',
  CycleCompleted = 'CycleCompleted',
}

export enum ETrainingStatus {
  None = 0,
  Ongoing = 1,
  Skipped = 99,
  Finished = 100,
}

export interface IWorkoutStatus {
  workoutId: number;
  index: number;
  finishedTimestamp: number;
  status: ETrainingStatus;
}

export interface ISessionStatus {
  sessionId: number;
  domainId: number;
  finishedTimestamp: number;
  status: ETrainingStatus;
  workouts: IWorkoutStatus[];
}

export interface ICycleStatus {
  trainingId: number;
  cycleId: number;
  startTimestamp: number;
  finishedTimestamp: number;
  sessions: ISessionStatus[];
}

export interface ICycleStatusApiResponse {
  filter: unknown;
  trainingcycles: ICycleStatus[];
}

export type TCycleStatusResponse = AxiosResponse<ICycleStatusApiResponse>;

export interface ITrainingStatistics {
  averageTrainingDaysPerWeek?: number | null;
  currentCycleId?: number | null;
  currentCycleLastSessionId?: number | null;
  currentCycleLength?: number | null;
  finishedCycles?: number;
  finishedSessions?: number;
  numberAssessments?: number;
  numberMigrated?: number;
  numberWorkouts?: number;
  firstTimestamp?: number;
  lastTimestamp?: number;
}

export interface ITrainingStatisticsApiResponse {
  trainingstatistics: ITrainingStatistics;
}

export type TTrainingStatisticsResponse = AxiosResponse<ITrainingStatisticsApiResponse>;

export interface IClientDBSync {
  syncTimestamp: number;
}

export interface IWorkoutDBStatus {
  cycleId: number;
  sessionId: number;
  workoutId: number;
  index: number;
  finishedTimestamp: number;
  status: ETrainingStatus;
}

export interface IWorkoutClientStatus extends IWorkoutDBStatus, IClientDBSync {
  cycleId: number;
  sessionId: number;
  workoutId: number;
  index: number;
  finishedTimestamp: number;
  status: ETrainingStatus;
  syncTimestamp: number;
}

export interface ISessionDBStatus {
  cycleId: number;
  sessionId: number;
  domainId: number;
  finishedTimestamp: number;
  status: ETrainingStatus;
}

export interface ISessionClientStatus extends ISessionDBStatus, IClientDBSync {
  cycleId: number;
  sessionId: number;
  domainId: number;
  finishedTimestamp: number;
  status: ETrainingStatus;
  syncTimestamp: number;
}

export interface ICycleDBStatus {
  trainingId: number;
  cycleId: number;
  startTimestamp: number;
  finishedTimestamp: number;
}

export interface ICycleClientStatus extends ICycleDBStatus, IClientDBSync {
  trainingId: number;
  cycleId: number;
  startTimestamp: number;
  finishedTimestamp: number;
  syncTimestamp: number;
}

export interface ITrainingDBStatus {
  currentCycleId: number;
  currentSessionId: number;
  currentCycleLength: number;
  finishedCycles: number;
  finishedSessions: number;
  numberWorkouts: number;
  firstTimestamp?: number;
  lastTimestamp?: number;
}

export interface ITrainingClientStatus extends ITrainingDBStatus, IClientDBSync {
  currentCycleId: number;
  currentSessionId: number;
  currentCycleLength: number;
  finishedCycles: number;
  finishedSessions: number;
  numberWorkouts: number;
  syncTimestamp: number;
}

export type TCycleSyncMap = Map<number, Set<number>>;

export interface IWorkoutDistribution {
  workoutId: number;
  targetDistribution: number;
  amount: number;
}

export interface IInitCyclesPayload {
  userId: number;
  isPremium: boolean;
}

export interface IUpdateStatsPayload {
  currentCycleId: number;
  currentSessionId: number;
  cycleLength: number;
  finishedCycles: number;
  finishedSessions: number;
  numberWorkouts: number;
  firstTimestamp?: number;
  lastTimestamp?: number;
}

export interface ICompleteWorkoutPayload {
  workoutId: number;
  newStatus: ETrainingStatus;
  workoutIndex: number;
}

export interface IUpdateCycleStatsPayload {
  startTimestamp: number;
  finishedTimestamp: number;
}

export interface ICreateAssessmentPayload {
  config: IAssessmentConfigOPXP;
  addCFQ: boolean;
}

export interface IReplaceCurrentWorkoutPayload {
  workoutId: number;
  workoutIndex?: number;
}

export interface IReplaceWorkoutPayload {
  sourceId: number;
  targetId: number;
}
